import { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsXCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useDefaultFieldDefs } from '../../../models/customFields/queries';

/**
 * Prompt shown when a deal is moved into a lost-type stage.
 *
 * The reason itself is a category picked from the org's configurable list
 * (Settings → Custom Fields → Deal); reports group by it, so it must stay a
 * closed set. The free-text note only annotates that category — it is never a
 * reason on its own. "Sonstiges" is the catch-all for anything off the list.
 *
 * Whether a category is mandatory follows the org's `isRequired` setting on the
 * same field definition. The parent owns closing: it keeps the modal open via
 * `isLoading` until the transition resolves, then unmounts it.
 */
const LossReasonModal = ({ isOpen, onClose, onConfirm, dealTitle, isLoading = false }) => {
	const [reason, setReason] = useState('');
	const [note, setNote] = useState('');

	const { data: defaultDefs } = useDefaultFieldDefs('deal');
	const lossReasonDef = useMemo(() => defaultDefs.find((f) => f.fieldKey === 'lossReason'), [defaultDefs]);

	const options = lossReasonDef?.selectOptions ?? [];
	const isRequired = lossReasonDef?.isRequired === true;
	const isMissingRequired = isRequired && !reason;

	// Reset both fields whenever the modal (re)opens so nothing stale lingers.
	useEffect(() => {
		if (isOpen) {
			setReason('');
			setNote('');
		}
	}, [isOpen]);

	const handleConfirm = () => {
		if (isMissingRequired) return;
		onConfirm({ lossReason: reason || null, lossReasonNote: note.trim() || null });
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="s" closeOnBackdrop={!isLoading}>
			<Panel.Header icon={BsXCircle} onClose={isLoading ? undefined : onClose}>
				Deal als verloren markieren
			</Panel.Header>

			<Panel.Body>
				{dealTitle && (
					<p className="text-sm text-bgBase-800 mb-3">
						Deal <span className="font-semibold text-text">„{dealTitle}“</span> wird als verloren markiert.
					</p>
				)}

				<label htmlFor="lossReason" className="block text-sm font-medium text-text mb-1.5">
					Verlustgrund{' '}
					{isRequired ? (
						<span className="text-warning-500">*</span>
					) : (
						<span className="font-normal text-bgBase-800">(optional)</span>
					)}
				</label>
				<Select
					id="lossReason"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					autoFocus
					disabled={isLoading}
				>
					<option value="">Auswählen …</option>
					{options.map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</Select>
				{options.length === 0 && (
					<p className="mt-1.5 text-xs text-caution-700">
						Es sind keine Verlustgründe konfiguriert. Sie können sie unter Einstellungen → Eigene Felder →
						Deal hinterlegen.
					</p>
				)}

				<label htmlFor="lossReasonNote" className="block text-sm font-medium text-text mt-4 mb-1.5">
					Anmerkung <span className="font-normal text-bgBase-800">(optional)</span>
				</label>
				<Textarea
					id="lossReasonNote"
					value={note}
					onChange={(e) => setNote(e.target.value)}
					maxLength={500}
					placeholder="z. B. Angebot des Wettbewerbers war 15 % günstiger"
					disabled={isLoading}
				/>
			</Panel.Body>

			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
					Abbrechen
				</Button>
				<Button
					type="button"
					onClick={handleConfirm}
					variant="danger"
					loading={isLoading}
					disabled={isLoading || isMissingRequired}
					title={isMissingRequired ? 'Bitte einen Verlustgrund auswählen' : undefined}
				>
					Als verloren markieren
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

LossReasonModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	dealTitle: PropTypes.string,
	isLoading: PropTypes.bool,
};

export default LossReasonModal;
